import useAxios from 'hooks/useAxios';

const BASE = '/nh-game/dashboard';

export const listAllRooms = async () => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/rooms`);
    return res.data;
  } catch (err) {
    console.error('Error listing rooms:', err);
    return [];
  }
};

export const getRoomLiveState = async (roomId) => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/rooms/${roomId}/live-state`);
    return { data: res.data, notFound: false };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) {
      return { data: null, notFound: true };
    }
    console.error('Error fetching live state:', err);
    return { data: null, notFound: false };
  }
};

export const getRoomEngineInfo = async (roomId) => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/rooms/${roomId}/engine-info`);
    return res.data;
  } catch (err) {
    console.error('Error fetching engine info:', err);
    return null;
  }
};

export const getRoomAutoBidSessions = async (roomId) => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/rooms/${roomId}/auto-bid-sessions`);
    return res.data;
  } catch (err) {
    console.error('Error fetching auto-bid sessions:', err);
    return [];
  }
};

export const getRoomSummary = async (roomId) => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/rooms/${roomId}/summary`);
    return res.data;
  } catch (err) {
    console.error('Error fetching room summary:', err);
    return null;
  }
};

export const getRoomAirdropHistory = async (roomId, limit = 20, offset = 0) => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/rooms/${roomId}/airdrop-history`, {
      params: { limit, offset }
    });
    return res.data;
  } catch (err) {
    console.error('Error fetching airdrop history:', err);
    return { data: [], total: 0 };
  }
};

export const getRoomNetMetrics = async (roomId, bucket = 'hour') => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/rooms/${roomId}/net-metrics`, {
      params: { bucket }
    });
    return res.data;
  } catch (err) {
    console.error('Error fetching net metrics:', err);
    return null;
  }
};

export const getRoomBlocks = async (roomId, limit = 20, offset = 0, excludeCoins) => {
  const api = useAxios();
  try {
    const params = { limit, offset };
    if (excludeCoins && excludeCoins.length > 0) params.excludeCoins = excludeCoins.join(',');
    const res = await api.get(`/nh-game/transparency/rooms/${roomId}/blocks`, {
      params
    });
    return res.data;
  } catch (err) {
    console.error('Error fetching blocks:', err);
    return { data: [], total: 0 };
  }
};

export const getRoomBids = async (roomId, limit = 20, offset = 0) => {
  const api = useAxios();
  try {
    const res = await api.get(`/nh-game/transparency/rooms/${roomId}/bids`, {
      params: { limit, offset }
    });
    return res.data;
  } catch (err) {
    console.error('Error fetching bids:', err);
    return { data: [], total: 0 };
  }
};

export const getRoomBossCycles = async (roomId, limit = 20, offset = 0) => {
  const api = useAxios();
  try {
    const res = await api.get(`/nh-game/transparency/rooms/${roomId}/boss-cycles`, {
      params: { limit, offset }
    });
    return res.data;
  } catch (err) {
    console.error('Error fetching boss cycles:', err);
    return { data: [], total: 0 };
  }
};

export const listBetLimitPresets = async () => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/bet-limit-presets`);
    return res.data;
  } catch (err) {
    console.error('Error fetching bet limit presets:', err);
    return [];
  }
};

export const listPastSessions = async (params = {}) => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/past-sessions`, { params });
    return res.data;
  } catch (err) {
    console.error('Error fetching past sessions:', err);
    return { data: [], total: 0 };
  }
};

export const listSessionSnapshots = async (params = {}) => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/sessions`, { params });
    return res.data;
  } catch (err) {
    console.error('Error fetching session snapshots:', err);
    return { data: [], total: 0 };
  }
};

export const getSessionSnapshot = async (snapshotId) => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/sessions/${snapshotId}`);
    return res.data;
  } catch (err) {
    console.error('Error fetching session snapshot:', err);
    return null;
  }
};

export const getNicehashBalance = async () => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/nicehash-balance`);
    return res.data;
  } catch (err) {
    console.error('Error fetching NiceHash balance:', err);
    return null;
  }
};

export const captureSnapshotBalance = async (snapshotId) => {
  const api = useAxios();
  try {
    const res = await api.post(`${BASE}/sessions/${snapshotId}/capture-balance`);
    return res.data;
  } catch (err) {
    console.error('Error capturing snapshot balance:', err);
    return null;
  }
};

export const getExchangeRates = async () => {
  const api = useAxios();
  try {
    const res = await api.get('/nh-game/exchange-rates');
    return res.data;
  } catch (err) {
    console.error('Error fetching exchange rates:', err);
    return null;
  }
};

// ── Session Export Helpers ────────────────────────────────────────────────────

export const downloadSessionCSV = async (snapshotId) => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/sessions/${snapshotId}/export-csv`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `session-${snapshotId.slice(0, 8)}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (err) {
    console.error('Error downloading CSV export:', err);
    return false;
  }
};

export const downloadSessionXLSX = async (snapshotId) => {
  const api = useAxios();
  try {
    const res = await api.get(`${BASE}/sessions/${snapshotId}/export-xlsx`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(
      new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `session-${snapshotId.slice(0, 8)}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (err) {
    console.error('Error downloading XLSX export:', err);
    return false;
  }
};

export const getSessionReportUrl = (snapshotId) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${baseUrl}/nh-game/public/session-report/${snapshotId}`;
};
